import { useEffect, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { DirectionProvider } from '@radix-ui/react-direction';
import { UndoProvider } from '@/design/components';
import { SettingsContext } from '@/access/SettingsContext';
import { useSettingsQuery } from '@/access/useSettings';
import { MeContext, useMeQuery } from '@/auth/useMe';
import { useSession } from '@/auth/useSession';
import { qk } from '@/lib/queryKeys';
import { supabase } from '@/lib/supabase';
import { useRuntimeManifest } from './useRuntimeManifest';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
    },
  });
}

/**
 * כניסה ויציאה משנות מה RLS מחזיר על אותן שאילתות. בלי ניקוי, המטמון
 * של לפני הכניסה נשאר בתוקף: מסך הכניסה טוען את app_settings כאנונימי
 * ומקבל רק את מפתחות המיתוג, וה staleTime של עשר דקות היה משאיר את
 * המערכת בלי modules, כלומר בלי תפריט.
 */
function useResetCacheOnAuthChange() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        void queryClient.invalidateQueries();
      }
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);
}

/**
 * כיבוי מודול מגיע לכל מי שהאפליקציה פתוחה אצלו, מיד.
 *
 * ההחלטה תמיד היתה גלובלית — שורה אחת ב app_settings — אבל
 * אפליקציה שכבר רצה החזיקה את התשובה הישנה עד שהמטמון שלה
 * התיישן. בפועל זה אומר שמנהל כיבה מסך, ראה אותו נעלם אצלו,
 * והעובדים המשיכו לראות אותו. אין שליטה אם צריך לקוות שכולם
 * יסגרו ויפתחו.
 *
 * זה אות ריענון ולא תוכן: מה שמגיע הוא "ההגדרות השתנו",
 * והלקוח קורא מחדש דרך הערוצים הרגילים, עם ה RLS שלו.
 */
function useLiveSettings() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('app-settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings' }, () => {
        void queryClient.invalidateQueries({ queryKey: qk.settings });
        void queryClient.invalidateQueries({ queryKey: qk.me });
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);
}

/** ההגדרות והעובד נטענים במקביל בעליית האפליקציה (מסמך התכנון, סעיף 4.1). */
function AppContexts({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const { settings, isSuccess: settingsLoaded } = useSettingsQuery();
  const meQuery = useMeQuery(session?.user.id);

  useResetCacheOnAuthChange();
  useLiveSettings();
  useRuntimeManifest(settings, settingsLoaded);

  const meState = useMemo(
    () => ({ me: meQuery.data ?? null, isLoading: Boolean(session) && meQuery.isPending }),
    [meQuery.data, meQuery.isPending, session],
  );

  return (
    <SettingsContext.Provider value={settings}>
      <MeContext.Provider value={meState}>
        <UndoProvider>{children}</UndoProvider>
      </MeContext.Provider>
    </SettingsContext.Provider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => makeQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <DirectionProvider dir="rtl">
        <AppContexts>{children}</AppContexts>
      </DirectionProvider>
    </QueryClientProvider>
  );
}
